// API Service Layer
// Centralized API communication for the frontend

import { getApiBaseUrl } from './apiBaseUrl';

export { getApiBaseUrl, normalizeApiBase, getPlayApiBaseUrl } from './apiBaseUrl';

export const API_BASE_URL = getApiBaseUrl();
if (process.env.NODE_ENV === 'development') {
  console.log('[API] Base URL:', API_BASE_URL);
}

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

const isPublicRoute = (pathname = '') => {
  return (
    pathname === '/' ||
    pathname === '/login' ||
    pathname.startsWith('/book/') ||
    pathname.startsWith('/play/') ||
    pathname.startsWith('/register/tournament/') ||
    pathname.startsWith('/enroll/')
  );
};

// Helper function to handle token expiry
const handleTokenExpiry = () => {
  // Clear token and user data
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
  
  // Keep public routes public even if a stale token exists.
  // This prevents shared booking links from forcing login.
  if (!isPublicRoute(window.location.pathname)) {
    // Store the current location to redirect back after login
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
  }
};

// Helper to get the active facility ID (set by FacilityContext) as string for request headers
const getActiveFacilityId = () => {
  return localStorage.getItem('active_facility_id');
};

/** Parse active facility for tenant-scoped localStorage; keeps BookingContext aligned with api request scope. */
export const getStoredActiveFacilityId = () => {
  try {
    const raw = localStorage.getItem('active_facility_id');
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  } catch {
    return null;
  }
};

/** Build query string without null/undefined/empty values — URLSearchParams would otherwise emit key=undefined */
const searchParamsFromObject = (obj = {}) =>
  new URLSearchParams(
    Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== ''))
  ).toString();

// Helper function to make API requests
export const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const facilityId = getActiveFacilityId();

  // `silentStatuses` lets callers treat specific HTTP statuses as an expected
  // outcome (e.g. GA-006: a 404 from dashboard-layout means "no saved layout").
  // Such responses resolve to null without being double-logged as errors.
  const { silentStatuses, ...fetchOptions } = options;
  const silent = Array.isArray(silentStatuses) ? silentStatuses : [];

  const config = {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(facilityId && { 'X-Facility-Id': facilityId }),
      ...fetchOptions.headers,
    },
  };
  
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    // Parse defensively — error responses (e.g. 404) may have an empty body.
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    
    if (!response.ok) {
      // Handle token expiry errors (401 Unauthorized or 403 Forbidden)
      if (response.status === 401 || response.status === 403) {
        const errorMessage = (data && data.message) || 'Authentication failed';
        
        // Check if it's a token expiry error
        if (errorMessage.includes('expired') || 
            errorMessage.includes('Invalid or expired token') ||
            errorMessage.includes('Access token required')) {
          console.warn('⚠️ Token expired or invalid. Redirecting to login...');
          handleTokenExpiry();
          throw new Error('Your session has expired. Please log in again.');
        }
      }

      // Expected status → resolve to null quietly (no error log, no throw).
      if (silent.includes(response.status)) {
        return null;
      }
      
      const msg = data?.message || 'API request failed';
      // Log internal details to the console for developers but never surface
      // them in the thrown message which ends up in user-facing toasts/alerts.
      const method = (config && config.method) ? config.method.toUpperCase() : 'GET';
      const facilityHeader = facilityId ? String(facilityId) : 'none';
      console.error(`[API] ${method} ${endpoint} → ${response.status} (x-facility-id: ${facilityHeader})`);
      throw new Error(msg);
    }
    
    return data;
  } catch (error) {
    // Re-throw if it's already a handled token expiry error
    if (error.message.includes('session has expired')) {
      throw error;
    }
    
    console.error('API Error:', error);
    throw error;
  }
};

// ============================================
// AUTHENTICATION API
// ============================================

export const authAPI = {
  login: async (username, password) => {
    return apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      headers: {
        'X-Client-Type': 'web',
      },
    });
  },
  
  register: async (userData) => {
    return apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },
  
  verify: async () => {
    return apiRequest('/auth/verify', {
      method: 'GET',
    });
  },

  logout: async () => {
    return apiRequest('/auth/logout', { method: 'POST' });
  },

  otpSend: async (phone) => {
    return apiRequest('/auth/otp/send', {
      method: 'POST',
      body: JSON.stringify({ phone }),
      headers: { 'X-Client-Type': 'web' },
    });
  },

  otpVerify: async (phone, otp) => {
    return apiRequest('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ phone, otp }),
      headers: { 'X-Client-Type': 'web' },
    });
  },

  forgotPassword: async (phone) => {
    return apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  },

  resetPassword: async (phone, otp, newPassword) => {
    return apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ phone, otp, newPassword }),
    });
  },

  registerOwner: async (registrationToken, name, facilityName, password, confirmPassword) => {
    return apiRequest('/auth/register-owner', {
      method: 'POST',
      body: JSON.stringify({
        registrationToken,
        name,
        facilityName,
        password,
        confirm_password: confirmPassword,
      }),
      headers: { 'X-Client-Type': 'web' },
    });
  },
};

// ============================================
// ANALYTICS (Super Admin only)
// ============================================

export const analyticsAPI = {
  getLoginStats: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiRequest(`/analytics/login-stats${q ? `?${q}` : ''}`);
  },
  getAuditLogs: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiRequest(`/analytics/audit-logs${q ? `?${q}` : ''}`);
  },
};

// ============================================
// BOOKINGS API
// ============================================

export const bookingsAPI = {
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    return apiRequest(`/bookings?${queryParams}`);
  },
  
  getById: async (id) => {
    return apiRequest(`/bookings/${id}`);
  },
  
  create: async (bookingData) => {
    return apiRequest('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  },
  
  update: async (id, updateData) => {
    return apiRequest(`/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  },
  
  cancel: async (id) => {
    return apiRequest(`/bookings/${id}`, {
      method: 'DELETE',
    });
  },
  
  checkAvailability: async (date, groundId, serviceId, slotId) => {
    const params = new URLSearchParams({
      date,
      ground_id: groundId,
      service_id: serviceId,
      slot_id: slotId,
    }).toString();
    return apiRequest(`/bookings/availability/check?${params}`);
  },
};

// ============================================
// PAYMENTS API
// ============================================

export const paymentsAPI = {
  record: async (paymentData) => {
    return apiRequest('/payments/record', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },
  
  getHistory: async (bookingId) => {
    return apiRequest(`/payments/history/${bookingId}`);
  },
  
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    return apiRequest(`/payments?${queryParams}`);
  },
};

// ============================================
// FACILITIES API
// ============================================

export const facilitiesAPI = {
  get: async () => {
    return apiRequest('/facilities');
  },

  getById: async (facilityId) => {
    return apiRequest(`/facilities?id=${encodeURIComponent(facilityId)}`);
  },

  getGrounds: async () => {
    const res = await apiRequest('/facilities/grounds');
    return res && res.success ? res : { success: true, data: [] };
  },

  addGround: async (name) => apiRequest('/facilities/grounds', {
    method: 'POST',
    body: JSON.stringify({ name }),
  }),

  renameGround: async (groundId, name) => apiRequest(`/facilities/grounds/${groundId}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  }),

  deleteGround: async (groundId) => apiRequest(`/facilities/grounds/${groundId}`, {
    method: 'DELETE',
  }),

  getServices: async (groundId = null, facilityId = null) => {
    const params = new URLSearchParams();
    if (groundId) params.append('ground_id', groundId);
    if (facilityId) params.append('facility_id', facilityId);
    const queryParams = params.toString();
    return apiRequest(`/facilities/services${queryParams ? '?' + queryParams : ''}`);
  },

  createService: async (facilityId, serviceData) => {
    return apiRequest(`/facilities/${facilityId}/services`, {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  },

  deleteService: async (serviceId) => {
    return apiRequest(`/facilities/services/${serviceId}`, {
      method: 'DELETE',
    });
  },
  
  updateService: async (serviceId, serviceData) => {
    return apiRequest(`/facilities/services/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  },
  
  create: async (facilityData) => {
    return apiRequest('/facilities', {
      method: 'POST',
      body: JSON.stringify(facilityData),
    });
  },
  
  update: async (id, facilityData) => {
    return apiRequest(`/facilities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(facilityData),
    });
  },

  updateLocation: async (facilityId, locationData) => {
    return apiRequest(`/facilities/${facilityId}/location`, {
      method: 'PATCH',
      body: JSON.stringify(locationData),
    });
  },
};

// ============================================
// WEATHER API
// ============================================

export const waitlistAPI = {
  getAll: async (filters = {}) => {
    const q = new URLSearchParams(filters).toString();
    return apiRequest(`/waitlist${q ? '?' + q : ''}`);
  },
  create: async (data) => {
    return apiRequest('/waitlist', { method: 'POST', body: JSON.stringify(data) });
  },
  promote: async (id, paymentData = {}) => {
    return apiRequest(`/waitlist/${id}/promote`, { method: 'PUT', body: JSON.stringify(paymentData) });
  },
  cancel: async (id) => {
    return apiRequest(`/waitlist/${id}/cancel`, { method: 'PUT' });
  },
};

export const weatherAPI = {
  getForFacility: async ({ lat, lng, city } = {}) => {
    const params = new URLSearchParams();
    if (lat != null && lng != null) {
      params.append('lat', lat);
      params.append('lng', lng);
    } else if (city) {
      params.append('city', city);
    } else {
      return { success: false, message: 'No location provided' };
    }
    return apiRequest(`/weather?${params.toString()}`);
  },
};

// ============================================
// CORE SERVICES API
// ============================================

export const coreServicesAPI = {
  getAll: async (type = null, category = null) => {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (category) params.append('category', category);
    const queryParams = params.toString();
    return apiRequest(`/core-services${queryParams ? '?' + queryParams : ''}`);
  },
  
  getById: async (id) => {
    return apiRequest(`/core-services/${id}`);
  },
  
  create: async (serviceData) => {
    return apiRequest('/core-services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  },
  
  update: async (id, serviceData) => {
    return apiRequest(`/core-services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  },
  
  delete: async (id) => {
    return apiRequest(`/core-services/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// USERS API
// ============================================

export const usersAPI = {
  getAll: async () => {
    return apiRequest('/users');
  },
  
  getById: async (id) => {
    return apiRequest(`/users/${id}`);
  },
  
  create: async (userData) => {
    const body = { ...userData };
    if (body.roleId != null) {
      body.role_id = Number(body.roleId);
      delete body.roleId;
    }
    if (Array.isArray(body.facilityIds)) {
      body.facility_ids = body.facilityIds.map((id) => Number(id));
      delete body.facilityIds;
    }
    return apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  update: async (id, userData) => {
    const body = { ...userData };
    if (body.roleId != null) {
      body.role_id = Number(body.roleId);
      delete body.roleId;
    }
    if (Array.isArray(body.facilityIds)) delete body.facilityIds;
    return apiRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  
  delete: async (id) => {
    return apiRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  },

  getFacilities: async (userId) => {
    return apiRequest(`/users/${userId}/facilities`);
  },

  setFacilities: async (userId, facilityIds, defaultFacilityId) => {
    return apiRequest(`/users/${userId}/facilities`, {
      method: 'PUT',
      body: JSON.stringify({
        facility_ids: facilityIds,
        default_facility_id: defaultFacilityId,
      }),
    });
  },

  getDashboardLayout: async (userId) => {
    // 404 = user has no saved layout yet; treat as a default-layout success
    // rather than an error (GA-006).
    return apiRequest(`/users/${userId}/dashboard-layout`, { silentStatuses: [404] });
  },

  saveDashboardLayout: async (userId, layout) => {
    return apiRequest(`/users/${userId}/dashboard-layout`, {
      method: 'PUT',
      body: JSON.stringify(layout),
    });
  },
};

// ============================================
// ROLES API
// ============================================

export const rolesAPI = {
  getAll: async () => {
    return apiRequest('/roles');
  },
  
  getById: async (id) => {
    return apiRequest(`/roles/${id}`);
  },
  
  create: async (roleData) => {
    return apiRequest('/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  },
  
  update: async (id, roleData) => {
    return apiRequest(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    });
  },
  
  delete: async (id) => {
    return apiRequest(`/roles/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// REPORTS API
// ============================================

export const reportsAPI = {
  monthlyBookings: async ({ month, year, include, page, limit } = {}) => {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (year) params.set('year', String(year));
    if (include) params.set('include', include);
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return apiRequest(`/reports/monthly-bookings${qs ? `?${qs}` : ''}`);
  },

  slotPayments: async ({ month } = {}) => {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    const qs = params.toString();
    return apiRequest(`/reports/slot-payments${qs ? `?${qs}` : ''}`);
  },

  transactions: async ({ from, to, start_date, end_date, page, limit } = {}) => {
    const params = new URLSearchParams();
    const start = from || start_date;
    const end = to || end_date;
    if (start) params.set('from', start);
    if (end) params.set('to', end);
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return apiRequest(`/reports/transactions${qs ? `?${qs}` : ''}`);
  },
};

// ============================================
// SUPPORT API
// ============================================

export const supportAPI = {
  submitInquiry: async (inquiryData) => {
    const res = await fetch(`${API_BASE_URL}/support/inquiry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(inquiryData),
    });
    return res.json();
  },

  createTicket: async (ticketData) => {
    return apiRequest('/support/tickets', {
      method: 'POST',
      body: JSON.stringify(ticketData),
    });
  },

  getTickets: async () => {
    return apiRequest('/support/tickets');
  },
};

export const surveyAPI = {
  submit: async (surveyData) => {
    const response = await fetch(`${API_BASE_URL}/survey`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(surveyData),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Survey submission failed');
    }
    return data;
  },
};

// ============================================
// CONFIGURATIONS API
// ============================================

export const configurationsAPI = {
  paymentConfig: () => apiRequest('/configurations/payment-config'),

  getSlotConfigurations: async () => {
    return apiRequest('/configurations/slots');
  },
  
  saveSlotConfiguration: async (configData) => {
    return apiRequest('/configurations/slots', {
      method: 'POST',
      body: JSON.stringify(configData),
    });
  },

  // §8 rule-based slots: the rule is the source of truth; the server
  // regenerates slots from it over a rolling horizon.
  getSlotRule: async (serviceId) => {
    return apiRequest(`/configurations/slots/rule?service_id=${serviceId}`);
  },

  saveSlotRule: async (ruleData) => {
    return apiRequest('/configurations/slots/rule', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    });
  },

  extendDateRange: async (newEndDate) => {
    return apiRequest('/configurations/slots/extend-date-range', {
      method: 'PUT',
      body: JSON.stringify({ new_end_date: newEndDate }),
    });
  },
  
  getIndividualSportsPrices: async (serviceId = null) => {
    const queryParams = serviceId ? `?service_id=${serviceId}` : '';
    return apiRequest(`/configurations/individual-sports-prices${queryParams}`);
  },
  
  saveIndividualSportsPrices: async (serviceId, prices) => {
    return apiRequest('/configurations/individual-sports-prices', {
      method: 'POST',
      body: JSON.stringify({ service_id: serviceId, prices }),
    });
  },
  
  updateSlotPrice: async (configuredSlotId, dayOfWeek, price) => {
    return apiRequest('/configurations/slots/price', {
      method: 'PATCH',
      body: JSON.stringify({
        configured_slot_id: configuredSlotId,
        day_of_week: dayOfWeek,
        price,
      }),
    });
  },

  getAll: async () => {
    return apiRequest('/configurations');
  },
};

// ============================================
// EQUIPMENT & OPERATIONS API
// ============================================

export const equipmentAPI = {
  list: async (filters = {}) => {
    const q = new URLSearchParams(filters).toString();
    return apiRequest(`/equipment${q ? '?' + q : ''}`);
  },
  get: async (id) => apiRequest(`/equipment/${id}`),
  create: async (data) => apiRequest('/equipment', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/equipment/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/equipment/${id}`, { method: 'DELETE' }),
  uploadPhoto: async (file) => {
    const token = localStorage.getItem('auth_token');
    const facilityId = localStorage.getItem('active_facility_id');
    const formData = new FormData();
    formData.append('photo', file);
    const response = await fetch(`${API_BASE_URL}/equipment/photo`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(facilityId && { 'X-Facility-Id': facilityId }),
      },
      body: formData,
    });
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to upload photo');
    }
    return data;
  },
  addService: async (id, data) => apiRequest(`/equipment/${id}/service`, { method: 'POST', body: JSON.stringify(data) }),
  updateService: async (equipmentId, serviceId, data) => apiRequest(`/equipment/${equipmentId}/service/${serviceId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getServiceHistory: async (id) => apiRequest(`/equipment/${id}/service-history`),
  addRunLog: async (id, data) => apiRequest(`/equipment/${id}/run-log`, { method: 'POST', body: JSON.stringify(data) }),
  getRunLogs: async (id, filters = {}) => {
    const q = new URLSearchParams(filters).toString();
    return apiRequest(`/equipment/${id}/run-log${q ? '?' + q : ''}`);
  },
  getTotalRuntime: async (id) => apiRequest(`/equipment/${id}/total-runtime`),
  getAnalytics: async (id) => apiRequest(`/equipment/${id}/analytics`),
  addFuelLog: async (id, data) => apiRequest(`/equipment/${id}/fuel-log`, { method: 'POST', body: JSON.stringify(data) }),
  getFuelLogs: async (id, filters = {}) => {
    const q = new URLSearchParams(filters).toString();
    return apiRequest(`/equipment/${id}/fuel-log${q ? '?' + q : ''}`);
  },
  updateFuelLog: async (equipmentId, logId, data) => apiRequest(`/equipment/${equipmentId}/fuel-log/${logId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getFuelSummary: async (id) => apiRequest(`/equipment/${id}/fuel-summary`),
};

export const inventoryAPI = {
  list: async (filters = {}) => {
    const q = searchParamsFromObject(filters);
    return apiRequest(`/inventory${q ? '?' + q : ''}`);
  },
  get: async (id) => apiRequest(`/inventory/${id}`),
  create: async (data) => apiRequest('/inventory', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/inventory/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/inventory/${id}`, { method: 'DELETE' }),
  getTransactions: async (id) => apiRequest(`/inventory/${id}/transactions`),
  addTransaction: async (id, data) => apiRequest(`/inventory/${id}/transactions`, { method: 'POST', body: JSON.stringify(data) }),
  getLowStockCount: async () => apiRequest('/inventory/low-stock-count'),
};

export const operationsAPI = {
  addDaily: async (data) => apiRequest('/operations/daily', { method: 'POST', body: JSON.stringify(data) }),
  getDaily: async (date) => apiRequest(`/operations/daily?date=${date || new Date().toISOString().slice(0, 10)}`),
  updateDaily: async (id, data) => apiRequest(`/operations/daily/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

export const dashboardAPI = {
  getEquipmentSummary: async () => apiRequest('/dashboard/equipment-summary'),
  getSummary: async () => apiRequest('/dashboard/summary'),
};

export const onboardingAPI = {
  getStatus: async () => apiRequest('/onboarding/status'),
  // Marks the wizard finished server-side: setup_incomplete → trial.
  complete: async () => apiRequest('/onboarding/complete', { method: 'POST' }),
};

export const profileAPI = {
  get: async () => apiRequest('/profile'),
  update: async (data) =>
    apiRequest('/profile', { method: 'PUT', body: JSON.stringify(data) }),
  setPassword: async (data) =>
    apiRequest('/profile/set-password', { method: 'POST', body: JSON.stringify(data) }),
  changePassword: async (data) =>
    apiRequest('/profile/change-password', { method: 'POST', body: JSON.stringify(data) }),
};

export const sportsAPI = {
  getAll: () => apiRequest('/sports'),
};

export const staffAPI = {
  list: (params = {}) => {
    const cleaned = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const q = new URLSearchParams(cleaned).toString();
    return apiRequest(`/staff${q ? '?' + q : ''}`);
  },
  getById: (id) => apiRequest(`/staff/${id}`),
  getLinkedSelf: () => apiRequest('/staff/linked-self', { silentStatuses: [404] }),
  create: (data) => apiRequest('/staff', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiRequest(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiRequest(`/staff/${id}`, { method: 'DELETE' }),
  getStats: (id) => apiRequest(`/staff/${id}/stats`),
  getAttendance: (id) => apiRequest(`/staff/${id}/attendance`),
  getSalary: (id) => apiRequest(`/staff/${id}/salary`),
  addAttendance: (data) => apiRequest('/staff/attendance', { method: 'POST', body: JSON.stringify(data) }),
  getAttendanceByDate: (date) => apiRequest(`/staff/attendance/by-date?date=${date || ''}`),
  addSalary: (data) => apiRequest('/staff/salary', { method: 'POST', body: JSON.stringify(data) }),
  getSalaryByPeriod: (month, year) => apiRequest(`/staff/salary/by-period?month=${month}&year=${year}`),
  markSalaryPaid: (salaryId) => apiRequest(`/staff/salary/${salaryId}/mark-paid`, { method: 'PATCH' }),
  generateSalaries: (month, year) => apiRequest('/staff/salary/generate', { method: 'POST', body: JSON.stringify({ month, year }) }),
  addAdvance: (data) => apiRequest('/staff/salary/advance', { method: 'POST', body: JSON.stringify(data) }),
  getAdvances: (filters = {}) => {
    const q = new URLSearchParams(filters).toString();
    return apiRequest(`/staff/salary/advances${q ? '?' + q : ''}`);
  },
  recoverAdvance: (id, recoverySalaryId) => apiRequest(`/staff/salary/advance/${id}/recover`, { method: 'PATCH', body: JSON.stringify({ recovery_salary_id: recoverySalaryId }) }),
};

export const expensesAPI = {
  list: (params = {}) => {
    const q = searchParamsFromObject(params);
    return apiRequest(`/expenses${q ? '?' + q : ''}`);
  },
  getById: (id) => apiRequest(`/expenses/${id}`),
  create: (data) => apiRequest('/expenses', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiRequest(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiRequest(`/expenses/${id}`, { method: 'DELETE' }),
  uploadReceipt: async (file) => {
    const token = localStorage.getItem('auth_token');
    const facilityId = localStorage.getItem('active_facility_id');
    const formData = new FormData();
    formData.append('receipt', file);
    const response = await fetch(`${API_BASE_URL}/expenses/receipt`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(facilityId && { 'X-Facility-Id': facilityId }),
      },
      body: formData,
    });
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to upload receipt');
    }
    return data;
  },
  getCategories: () => apiRequest('/expenses/categories'),
  createCategory: (data) => apiRequest('/expenses/categories', { method: 'POST', body: JSON.stringify(data) }),
  getSummary: (month, year) => apiRequest(`/expenses/summary?month=${month}&year=${year}`),
  getByCategory: (month, year) => apiRequest(`/expenses/by-category?month=${month}&year=${year}`),
  getMonthlyTrend: (months = 12) => apiRequest(`/expenses/monthly-trend?months=${months}`),
};

export const selfieAttendanceAPI = {
  getHistory: (staffId, date) => {
    const q = date ? `?date=${date}` : '';
    return apiRequest(`/selfie-attendance/${staffId}${q}`);
  },
  getReport: ({ from, to, date, staffId } = {}) => {
    const params = new URLSearchParams();
    if (date) {
      params.set('date', date);
    } else {
      if (from) params.set('from', from);
      if (to) params.set('to', to);
    }
    if (staffId) params.set('staff_id', staffId);
    return apiRequest(`/selfie-attendance/report/all?${params.toString()}`);
  },
};

export default {
  auth: authAPI,
  bookings: bookingsAPI,
  payments: paymentsAPI,
  facilities: facilitiesAPI,
  coreServices: coreServicesAPI,
  users: usersAPI,
  roles: rolesAPI,
  reports: reportsAPI,
  support: supportAPI,
  survey: surveyAPI,
  configurations: configurationsAPI,
  equipment: equipmentAPI,
  inventory: inventoryAPI,
  operations: operationsAPI,
  dashboard: dashboardAPI,
  staff: staffAPI,
  expenses: expensesAPI,
  analytics: analyticsAPI,
  selfieAttendance: selfieAttendanceAPI,
};

/**
 * Notifications API.
 */
export const notificationsAPI = {
  /** List notifications (limit, offset, unread) */
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiRequest(`/notifications${qs ? `?${qs}` : ''}`);
  },

  /** Fast badge-only endpoint */
  unreadCount: () => apiRequest('/notifications/unread-count'),

  /** Mark one notification as read */
  markRead: (id) => apiRequest(`/notifications/${id}/read`, { method: 'PUT' }),

  /** Mark all unread as read */
  markAllRead: () => apiRequest('/notifications/read-all', { method: 'PUT' }),

  /** Create a notification (internal/admin) */
  create: (payload) =>
    apiRequest('/notifications', { method: 'POST', body: JSON.stringify(payload) }),
};

/**
 * Booking requests (facility approval workflow).
 */
export const bookingRequestsAPI = {
  /** List requests, optionally filtered by status ('pending' | 'approved' | 'rejected' | ''). */
  list: (status) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiRequest(`/booking-requests${qs}`);
  },

  /** Approve a request with payment details. */
  approve: (id, { totalAmount = 0, advanceAmount = 0, paymentMode = 'cash' } = {}) =>
    apiRequest(`/booking-requests/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({
        total_amount: totalAmount,
        advance_amount: advanceAmount,
        payment_mode: paymentMode,
      }),
    }),

  /** Reject a request with an optional reason. */
  reject: (id, reason = '') =>
    apiRequest(`/booking-requests/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),
};

